// /profile/connections/import-contacts — people from your phone contacts who
// are on Sotravel. App only: the native Connections screen reads the contacts
// and posts them back ("contactsImported"), and Connections routes here. An
// account page (pages/account/kit.js); back returns to Connections.
import React, { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { useTranslation } from "react-i18next";
import { Search, Users } from "lucide-react";
import authAxios from "../../authAxios";
import { useIsMobile } from "../../hooks/responsive";
import UserProfileBottomSheet from "./components/UserProfileBottomSheet";
import { getDisplayName } from "../../utils/userDisplayName";
import { openNativeProfile } from "../../utils/nativeProfileBridge";
import { Button, EmptyState, Skeleton } from "../../pages/manage/ui";
import { AccountPage } from "../../pages/account/kit";

const ImportContacts = () => {
  const isMobile = useIsMobile();
  const isInApp = window.isInApp;

  if (isMobile && isInApp) {
    return <ImportContactsPage />;
  }

  return <Navigate to="/profile/connections" />;
};

const ContactRow = ({ user, isFollowing: initiallyFollowing }) => {
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(!!initiallyFollowing);
  const [showUserProfile, setShowUserProfile] = useState(false);

  const toggleFollow = async (e) => {
    e.stopPropagation();
    const was = isFollowing;
    // Optimistically update UI first
    setIsFollowing(!was);
    try {
      if (was) await authAxios.post(`/v1/social/unfollowUser/${user.id}`);
      else await authAxios.post(`/v1/social/followUser/${user.id}`);
    } catch (error) {
      // Revert optimistic update on error
      setIsFollowing(was);
      console.error("Error updating follow status:", error);
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-border-b tw-border-[var(--hairline)] tw-py-3"
        onClick={() => {
          // Inside the Expo app, hand off to the native profile screen
          // instead of opening the bottom sheet.
          if (openNativeProfile(user.id)) return;
          setShowUserProfile(true);
        }}
      >
        <img src={user.image} alt="" className="tw-size-11 tw-shrink-0 tw-rounded-full tw-bg-[var(--fill-strong)] tw-object-cover" />
        <p className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[15px] tw-font-semibold">{getDisplayName(user)}</p>
        <Button size="sm" variant={isFollowing ? "light" : "default"} onClick={toggleFollow}>
          {isFollowing ? t("following", { defaultValue: "Following" }) : t("follow", { defaultValue: "Follow" })}
        </Button>
      </div>
      <UserProfileBottomSheet showUserProfile={showUserProfile} setShowUserProfile={setShowUserProfile} userId={user.id} />
    </>
  );
};

const ImportContactsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Get emails and phone numbers from navigation state
  const emails = location.state?.emails || [];
  const phoneNumbers = location.state?.phoneNumbers || [];

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);

  const [following, setFollowing] = useState([]);
  const [loadingFollowing, setLoadingFollowing] = useState(false);

  async function getFollowing() {
    setLoadingFollowing(true);
    const { data: followingList } = await authAxios.get("/v1/social/getFollowing");
    setFollowing(followingList);
    setLoadingFollowing(false);
  }

  useEffect(() => {
    getFollowing();
  }, []);

  const followingHash = following.reduce((acc, user) => {
    acc[user.following_id] = {
      is_approved: user.is_approved,
    };
    return acc;
  }, {});

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await authAxios.post(`/v1/mobileapp/getUsersFromContacts`, {
          emails,
          phoneNumbers,
        });

        const formattedContacts = response.data.map((user) => ({
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name || "",
          display_name: user.display_name,
          image: user.image || `https://api.dicebear.com/6.x/initials/svg?seed=${user.email || user.phone_number}`,
        }));

        setContacts(formattedContacts);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      } finally {
        setLoadingContacts(false);
      }
    };

    fetchContacts();
    // The contact lists come from navigation state once per visit.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchQuery, setSearchQuery] = useState("");
  const filteredContacts = contacts.filter((contact) => getDisplayName(contact).toLowerCase().includes(searchQuery.toLowerCase()));

  const loading = loadingContacts || loadingFollowing;

  return (
    <AccountPage
      title={t("account_import_contacts_title", { defaultValue: "Friends from your contacts" })}
      description={t("account_import_contacts_desc", { defaultValue: "People you know who are already on Sotravel." })}
      back={{ fallback: "/profile/connections" }}
    >
      <div className="tw-relative">
        <Search className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-size-4 tw--translate-y-1/2 tw-text-muted-foreground" />
        <input
          type="search"
          placeholder={t("account_search_contacts", { defaultValue: "Search contacts" })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="tw-h-11 tw-w-full tw-rounded-[var(--r-md)] tw-bg-[var(--fill)] tw-pl-10 tw-pr-3.5 tw-text-base placeholder:tw-text-muted-foreground/70 focus:tw-bg-background focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring/40"
        />
      </div>

      <div className="tw-mt-2">
        {loading ? (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-py-2">
            <Skeleton className="tw-h-14 tw-w-full" />
            <Skeleton className="tw-h-14 tw-w-full" />
            <Skeleton className="tw-h-14 tw-w-full" />
          </div>
        ) : filteredContacts.length > 0 ? (
          <Virtuoso
            useWindowScroll
            totalCount={filteredContacts.length}
            itemContent={(index) => <ContactRow key={filteredContacts[index].id} user={filteredContacts[index]} isFollowing={followingHash[filteredContacts[index].id]} />}
          />
        ) : (
          <div className="tw-mt-2 tw-rounded-[var(--r-card)] tw-bg-[var(--fill)]">
            <EmptyState compact icon={Users} title={t("account_no_contacts_found", { defaultValue: "No contacts found" })} />
          </div>
        )}
      </div>
    </AccountPage>
  );
};

export default ImportContacts;
